import React from 'react';
import { Typography, Layout, Divider, Flex, List, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { LanguageSelect } from '../../common/components/languageSelect/languageSelect';
import { styles, getPrivacyData, getTermsData } from './helper';

const { Title, Paragraph, Text } = Typography;
const { Content } = Layout;

export default function PrivacyAndTermsPage() {
    const { t } = useTranslation(['termAndConditions']);

    const data = getPrivacyData(t);
    const termsData = getTermsData(t);

    return (
        <Layout>
            <Content style={styles.container}>
                <Flex justify="space-between" align="center">
                    <Button type="primary" size="large" href="/" style={styles.button}>
                        {data.backToLogin}
                    </Button>
                    <LanguageSelect i18n={i18n} isUseRequest={false} useUrlLang={true} />
                </Flex>
                <>
                    <Flex justify="space-between" align="center" gap={10}>
                        <Title level={2} aria-label="Privacy Policy" style={styles.mainTitle}>
                            {t('termAndConditions:privacy.title')}
                        </Title>
                        <Text style={styles.updateDate}>{t('termAndConditions:privacy.lastUpdated')}</Text>
                    </Flex>

                    <div>
                        <Title level={4} style={styles.sectionTitle}>
                            {data.intro.title}
                        </Title>
                        <Text style={styles.text}>{data.intro.text}</Text>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.personalData.title}
                        </Title>
                        <Flex vertical gap={10}>
                            <Text style={styles.paragraph}>{data.personalData.text}</Text>
                            <List style={styles.list}>
                                {data.personalData.list.map(item => (
                                    <List.Item key={item.title}>
                                        <List.Item.Meta
                                            title={
                                                <span style={styles.listItemMeta}>{item.title + ': ' + item.text}</span>
                                            }
                                            avatar={<span style={styles.bullet}>●</span>}
                                        />
                                    </List.Item>
                                ))}
                            </List>
                        </Flex>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.purposeOfProcessing.title}
                        </Title>
                        <Text style={styles.paragraph}>{data.purposeOfProcessing.text}</Text>
                        <List itemLayout="horizontal">
                            {data.purposeOfProcessing.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={<span style={styles.listItemMeta}>{item.text}</span>}
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.transferOfData.title}
                        </Title>
                        <Text style={styles.paragraph}>{data.transferOfData.text}</Text>
                        <List itemLayout="horizontal">
                            {data.transferOfData.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.rights.title}
                        </Title>
                        <Text style={styles.paragraph}>{data.rights.text}</Text>
                        <List itemLayout="horizontal">
                            {data.rights.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.storagePeriod.title}
                        </Title>
                        <Paragraph>
                            <List itemLayout="horizontal">
                                <List.Item>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={<span style={styles.listItemMeta}>{data.storagePeriod.text}</span>}
                                    />
                                </List.Item>
                            </List>
                        </Paragraph>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.security.title}
                        </Title>
                        <Paragraph>
                            <Text style={styles.text}>{data.security.text}</Text>
                            <List itemLayout="horizontal">
                                {data.security.list.map(item => (
                                    <List.Item key={item.title}>
                                        <List.Item.Meta
                                            avatar={<span style={styles.bullet}>●</span>}
                                            title={<span style={styles.listItemMeta}>{item.title}</span>}
                                        />
                                    </List.Item>
                                ))}
                            </List>
                        </Paragraph>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.cookies.title}
                        </Title>
                        <Paragraph>
                            <Text style={styles.text}>{data.cookies.text}</Text>
                            <List itemLayout="horizontal">
                                {data.cookies.list.map(item => (
                                    <List.Item key={item.title}>
                                        <List.Item.Meta
                                            avatar={<span style={styles.bullet}>●</span>}
                                            title={
                                                <span style={styles.listItemMeta}>{item.title + ': ' + item.text}</span>
                                            }
                                        />
                                    </List.Item>
                                ))}
                            </List>
                        </Paragraph>

                        <Title level={4} style={styles.sectionTitle}>
                            {data.complaints.title}
                        </Title>
                        <Paragraph>
                            <Text style={styles.text}>{data.complaints.text}</Text>
                            <List itemLayout="horizontal">
                                {data.complaints.list.map(item => (
                                    <List.Item key={item.title}>
                                        <List.Item.Meta
                                            avatar={<span style={styles.bullet}>●</span>}
                                            title={
                                                <span style={styles.listItemMeta}>{item.title + ': ' + item.text}</span>
                                            }
                                        />
                                    </List.Item>
                                ))}
                            </List>
                        </Paragraph>
                    </div>

                    <Divider />

                    <Flex justify="space-between" align="center" gap={10}>
                        <Title level={2} aria-label="Terms of Service" style={styles.mainTitle}>
                            {t('termAndConditions:terms.title')}
                        </Title>
                        <Text style={styles.updateDate}>{t('termAndConditions:terms.lastUpdated')}</Text>
                    </Flex>

                    <div>
                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.intro.title}
                        </Title>
                        <Text style={styles.text}>{termsData.intro.text}</Text>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.acceptance.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.acceptance.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.acceptance.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.serviceDescription.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.serviceDescription.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.serviceDescription.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.registration.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.registration.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.registration.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.responsibilities.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.responsibilities.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.responsibilities.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.payments.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.payments.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.payments.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.prohibited.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.prohibited.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.prohibited.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.liability.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.liability.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.liability.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.termination.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.termination.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.termination.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>

                        <Title level={4} style={styles.sectionTitle}>
                            {termsData.contacts.title}
                        </Title>
                        <Text style={styles.paragraph}>{termsData.contacts.text}</Text>
                        <List itemLayout="horizontal">
                            {termsData.contacts.list.map(item => (
                                <List.Item key={item.title}>
                                    <List.Item.Meta
                                        avatar={<span style={styles.bullet}>●</span>}
                                        title={
                                            <span style={styles.listItemMeta}>
                                                {item.title} {item.text}
                                            </span>
                                        }
                                    />
                                </List.Item>
                            ))}
                        </List>
                    </div>
                </>
            </Content>
        </Layout>
    );
}
